import {
  MessagesSquare,
  Bot,
  Clock,
  Database,
  FileText,
  Folder as FolderIcon,
  Workflow as WorkflowIcon,
  type LucideIcon,
} from 'lucide-react';
import { ResourceType } from '@cotera/api';
import { resourceKind, type ResourceKindMeta } from '@cotera/sdk/core';

/**
 * The icon for each resource type. Lives here rather than on the resource kind
 * because the SDK is shared with the server and cannot depend on React.
 *
 * Written as a mapped type over {@link ResourceType} so a new type is a compile
 * error here, the same way it is in the kind registry.
 */
export const RESOURCE_TYPE_ICON: { readonly [K in ResourceType]: LucideIcon } =
  {
    [ResourceType.Chat]: MessagesSquare,
    [ResourceType.Agent]: Bot,
    [ResourceType.Folder]: FolderIcon,
    [ResourceType.Document]: FileText,
    [ResourceType.Workflow]: WorkflowIcon,
    [ResourceType.Dataset]: Database,
    [ResourceType.WorkflowTrigger]: Clock,
  };

/**
 * Every resource type except folders.
 *
 * A domain constraint, not a gap in {@link RESOURCE_TYPE_CONFIG} — folders have
 * their own archive action, page shell and share flow, so the generic ones are
 * declared as not accepting them. (This used to be the config's key type, which
 * is why callers also had to hand-code the folder route it left out.)
 */
export type NonFolderResourceType = Exclude<ResourceType, ResourceType.Folder>;

export type ResourceTypeConfig = ResourceKindMeta & {
  /** Lucide icon component. */
  readonly icon: LucideIcon;
};

/**
 * Per-type display and routing config: the shared {@link ResourceKind} plus this
 * app's icon.
 *
 * Covers every {@link ResourceType}, folders included. It used to be keyed by a
 * `NonFolderResourceType`, which is why callers all carried an `=== Folder`
 * branch to supply the segment it was missing.
 */
export const RESOURCE_TYPE_CONFIG: {
  readonly [K in ResourceType]: ResourceTypeConfig;
} = Object.fromEntries(
  Object.values(ResourceType).map((type) => [
    type,
    { ...resourceKind(type), icon: RESOURCE_TYPE_ICON[type] },
  ])
) as { readonly [K in ResourceType]: ResourceTypeConfig };
